import { Autocomplete, Button, Grid, Popover, TextField } from "@mui/material";
import { Fragment, useState } from "react";
import { Vocabulary } from "../../Utils/Vocabulary";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { CustomFilterValidationSchema } from "../../Utils/Models";
import { FilterAlt } from "@mui/icons-material";
import { FilterConfiguratorProps } from "../../Utils/Types";
import { RuleOptions } from "../../Utils/Constants";
import useCustomSearchParam from "../../Hooks/useCustomSearchParam";

export default function FilterConfigurator(props: FilterConfiguratorProps) {
  const { model } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const customSearchParams = useCustomSearchParam();
  const { control, formState, getValues } = useForm({
    resolver: yupResolver(CustomFilterValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  /**
   *
   * @param event
   */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   *
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   *
   */
  const handleAddNewFilter = () => {
    customSearchParams.addParamToQueryString(
      getValues("property"),
      `${encodeURIComponent(getValues("rule"))}-${getValues("threshold")}`
    );
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  return (
    <Fragment>
      <Button
        style={{ height: "100%" }}
        startIcon={<FilterAlt />}
        aria-describedby={id}
        variant="contained"
        color="header"
        onClick={handleClick}
      >
        {Vocabulary.customFilter}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Grid container spacing={2} sx={{ padding: "20px" }}>
          <Grid item xs={12} md={12} lg={12}>
            <Controller
              name="property"
              control={control}
              defaultValue={undefined}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo={false}
                  disablePortal
                  noOptionsText={Vocabulary.noResultsFound}
                  fullWidth
                  onChange={(event, value) => field.onChange(value)}
                  value={getValues("property")}
                  options={Object.keys(model)}
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionLabel={(option) => Vocabulary[option] || option}
                  id="property"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={error?.message}
                      name="property"
                      size="small"
                      required
                      variant="outlined"
                      inputRef={ref}
                      label={Vocabulary.property}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Controller
              name="rule"
              control={control}
              defaultValue={undefined}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo={false}
                  disablePortal
                  noOptionsText={Vocabulary.noResultsFound}
                  fullWidth
                  onChange={(event, value) => field.onChange(value?.value)}
                  options={RuleOptions}
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionLabel={(option: any) => option.label}
                  id="rule"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={error?.message}
                      name="rule"
                      required
                      size="small"
                      variant="outlined"
                      inputRef={ref}
                      label={Vocabulary.rule}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Controller
              name={"threshold"}
              control={control}
              render={({ field: props }) => (
                <TextField
                  {...props}
                  variant="outlined"
                  name="threshold"
                  size="small"
                  id="threshold"
                  required
                  label={Vocabulary.name}
                  fullWidth
                  error={typeof formState.errors.threshold === "object"}
                  helperText={formState.errors.threshold?.message?.toString()}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Button onClick={handleAddNewFilter} variant="contained">
              {Vocabulary.ok}
            </Button>
          </Grid>
        </Grid>
      </Popover>
    </Fragment>
  );
}
